import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Building2 } from 'lucide-react';
import { useBusinessInfo } from '../lib/businessInfo';
import { BOOK_PATH } from '../lib/routes';

const LOGO = "https://customer-assets.emergentagent.com/job_buraq-transport/artifacts/0a9wh5vw_0E081036-6309-4011-9127-0A9FE497D0F8.png";

const CONTACTS = [
  { icon: Mail, label: 'Email', value: 'hello@rideburaq.com', href: 'mailto:hello@rideburaq.com' },
  { icon: Phone, label: 'Phone', value: '+1 (929) 560-6305', href: 'tel:+19295606305' },
  { icon: MapPin, label: 'Service Area', value: 'New York · New Jersey · Connecticut', href: null },
];

const Contact = () => {
  const navigate = useNavigate();
  const biz = useBusinessInfo();
  const contacts = [
    ...CONTACTS,
    { icon: Building2, label: 'Business Address', value: `${biz.brand}, ${biz.address}`, href: null },
  ];

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-orange-600/15 blur-[120px]" />

      <div className="relative w-full max-w-3xl mx-auto px-5 lg:px-8 pb-16">
        <header className="flex items-center justify-between py-6 lg:py-8">
          <button
            data-testid="back-btn"
            onClick={() => navigate(-1)}
            className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-gray-300 hover:text-white hover:border-orange-500/40 active:scale-95 transition-all"
            aria-label="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <button
            data-testid="logo-home"
            onClick={() => navigate('/')}
            className="flex items-center gap-2.5 active:scale-95 transition-transform"
            aria-label="Home"
          >
            <img src={LOGO} alt="RideBuraq" className="w-8 h-8 object-contain" />
            <span className="text-[11px] font-semibold tracking-[0.35em] text-gray-400 uppercase">RideBuraq</span>
          </button>
        </header>

        <div className="pt-4 pb-8">
          <p className="text-[11px] tracking-[0.3em] uppercase text-orange-400/80 mb-2">Contact Us</p>
          <h1 className="text-4xl lg:text-5xl font-semibold tracking-tight leading-none">We're Here for You</h1>
          <p className="text-gray-500 text-sm lg:text-base mt-3">Reach our concierge team any time — day or night.</p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] divide-y divide-white/5">
          {contacts.map((c, i) => {
            const Inner = (
              <>
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shrink-0">
                  <c.icon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-[11px] tracking-[0.2em] uppercase text-gray-500">{c.label}</p>
                  <p className="text-white font-medium">{c.value}</p>
                </div>
              </>
            );
            return c.href ? (
              <a key={i} href={c.href} data-testid={`contact-${c.label.toLowerCase()}`} className="flex items-center gap-4 px-4 py-4 hover:bg-white/[0.03] transition-colors">
                {Inner}
              </a>
            ) : (
              <div key={i} className="flex items-center gap-4 px-4 py-4">
                {Inner}
              </div>
            );
          })}
        </div>

        <button
          data-testid="contact-reserve-btn"
          onClick={() => navigate(BOOK_PATH)}
          className="mt-8 w-full sm:w-auto sm:px-16 mx-auto rounded-full py-4 text-base font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 shadow-lg shadow-orange-900/30 hover:shadow-orange-500/30 active:scale-[0.99] transition-all flex items-center justify-center"
        >
          Reserve Your Ride
        </button>
      </div>
    </div>
  );
};

export default Contact;
